import type { Locale } from '@xdocs/i18n';

/**
 * STOP-GAP local catalog for the `adminAssets:` scope (F60), following the
 * `shares:`/`search:` precedent — see `apps/web/src/routes/shares/shares.i18n.ts`.
 * NOT a second i18n runtime (E18/§13.3 ban); `packages/i18n/**` is outside
 * this task's ownership.
 *
 * OPEN ISSUE: fold into `packages/i18n/src/catalogs/{en,ro}/admin.json`.
 */
const en = {
  'page.title': 'Assets / moderation',
  'page.description': 'Inspect, unpublish, or remove ANY user’s documents, shares, and files.',
  'filter.type.label': 'Type',
  'filter.type.all': 'All types',
  'filter.moderation.label': 'Moderation status',
  'filter.moderation.all': 'Any status',
  'type.document': 'Document',
  'type.share': 'Share',
  'type.file': 'File',
  'moderation.pending': 'Pending',
  'moderation.approved': 'Approved',
  'moderation.rejected': 'Rejected',
  'moderation.none': 'N/A',
  'list.column.title': 'Title',
  'list.column.type': 'Type',
  'list.column.owner': 'Owner',
  'list.column.moderation': 'Moderation',
  'list.column.createdAt': 'Created',
  'list.column.actions': 'Actions',
  'list.empty.title': 'No assets match these filters',
  'list.empty.description': 'Adjust the type or moderation filters to see more.',
  'list.error.title': "Couldn't load assets.",
  'list.noResults.title': 'No assets match “{query}”',
  'list.searchLabel': 'Search assets',
  'list.searchPlaceholder': 'Search by title…',
  'detail.title': 'Asset',
  'detail.field.owner': 'Owner',
  'detail.field.type': 'Type',
  'detail.field.moderation': 'Moderation status',
  'detail.field.created': 'Created',
  'detail.action.unpublish': 'Unpublish',
  'detail.action.remove': 'Remove',
  'action.inspect': 'Inspect',
  'action.unpublish': 'Unpublish',
  'action.remove': 'Remove',
  'unpublish.title': 'Unpublish',
  'unpublish.description': 'Removes this asset from discovery. Reversible — the owner (or you) can re-publish it later.',
  'remove.title': 'Remove',
  'remove.description': 'Takes this asset down entirely. This is a severe action — type the title below to confirm.',
  'reason.label': 'Reason (for the audit log)',
  'reason.placeholder': 'Why is this being taken down…',
  'typedConfirm.label': 'Type the asset title to confirm',
  'typedConfirm.mismatch': 'Doesn’t match the asset title.',
  'action.confirm': 'Confirm',
  'action.cancel': 'Cancel',
  'toast.unpublished.action': 'Unpublished',
  'toast.removed.action': 'Removed',
} as const;

const ro = {
  'page.title': 'Assets / moderare',
  'page.description': 'Inspectează, retrage sau elimină documente, distribuiri și fișiere ale ORICĂRUI utilizator.',
  'filter.type.label': 'Tip',
  'filter.type.all': 'Toate tipurile',
  'filter.moderation.label': 'Stare moderare',
  'filter.moderation.all': 'Orice stare',
  'type.document': 'Document',
  'type.share': 'Distribuire',
  'type.file': 'Fișier',
  'moderation.pending': 'În așteptare',
  'moderation.approved': 'Aprobat',
  'moderation.rejected': 'Respins',
  'moderation.none': 'N/A',
  'list.column.title': 'Titlu',
  'list.column.type': 'Tip',
  'list.column.owner': 'Proprietar',
  'list.column.moderation': 'Moderare',
  'list.column.createdAt': 'Creat',
  'list.column.actions': 'Acțiuni',
  'list.empty.title': 'Niciun conținut nu corespunde acestor filtre',
  'list.empty.description': 'Ajustează filtrele de tip sau moderare pentru mai multe rezultate.',
  'list.error.title': 'Nu am putut încărca conținuturile.',
  'list.noResults.title': 'Niciun conținut nu se potrivește cu „{query}”',
  'list.searchLabel': 'Caută conținuturi',
  'list.searchPlaceholder': 'Caută după titlu…',
  'detail.title': 'Conținut',
  'detail.field.owner': 'Proprietar',
  'detail.field.type': 'Tip',
  'detail.field.moderation': 'Stare moderare',
  'detail.field.created': 'Creat',
  'detail.action.unpublish': 'Retrage',
  'detail.action.remove': 'Elimină',
  'action.inspect': 'Inspectează',
  'action.unpublish': 'Retrage',
  'action.remove': 'Elimină',
  'unpublish.title': 'Retrage',
  'unpublish.description': 'Elimină acest conținut din descoperire. Reversibil — proprietarul (sau tu) îl poate republica ulterior.',
  'remove.title': 'Elimină',
  'remove.description': 'Elimină definitiv acest conținut. Este o acțiune severă — scrie titlul mai jos pentru confirmare.',
  'reason.label': 'Motiv (pentru jurnalul de audit)',
  'reason.placeholder': 'De ce este eliminat…',
  'typedConfirm.label': 'Scrie titlul conținutului pentru confirmare',
  'typedConfirm.mismatch': 'Nu se potrivește cu titlul conținutului.',
  'action.confirm': 'Confirmă',
  'action.cancel': 'Anulează',
  'toast.unpublished.action': 'Retras',
  'toast.removed.action': 'Eliminat',
} as const;

export const ADMIN_ASSETS_CATALOG: Record<Locale, Record<AdminAssetsCatalogKey, string>> = { en, ro };
export type AdminAssetsCatalogKey = keyof typeof en;
